import { Box, Button, HStack, Text } from '@chakra-ui/react';
import { ArrowDownLeft, Phone, Video, X } from 'lucide-react';
import { useCall } from '../../hooks/useCall';
import type { ChatMessage } from './messageUtils';
import { appLocale } from '../../lib/format';
import { useT } from '../../i18n';

type T = ReturnType<typeof useT>;

interface CallMetadata {
  callId?: string;
  callType?: 'voice' | 'video';
  outcome?: 'completed' | 'declined' | 'cancelled' | 'missed';
  durationSeconds?: number;
}

export function callMetadata(message: ChatMessage): CallMetadata | null {
  if (message.type !== 'system') return null;
  const meta = (message.metadata ?? {}) as CallMetadata;
  return meta.callId ? meta : null;
}

// "Today, 12:10 pm", "Yesterday, 7:15 pm", "Mon 4 Oct, 9:30 am".
function whenLabel(iso: string, t: T, now = new Date()): string {
  const date = new Date(iso);
  const time = date.toLocaleTimeString(appLocale(), {
    hour: 'numeric',
    minute: '2-digit',
  });
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === now.toDateString())
    return t('chat.call.today', { time });
  if (date.toDateString() === yesterday.toDateString())
    return t('chat.call.yesterday', { time });
  const day = date.toLocaleDateString(appLocale(), {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
  return `${day}, ${time}`;
}

function minutes(t: T, seconds = 0): string {
  if (seconds < 60) return t('chat.call.seconds', { n: Math.max(1, seconds) });
  return t('chat.call.minutes', { n: Math.round(seconds / 60) });
}

// A call's summary in the timeline (Living Room "call summaries"): a small
// centred pill, with "Call back" on a call you missed. The server posts these
// as system messages from the caller when a call ends (M19).
export function CallSummary({
  message,
  isOwn,
  meta,
}: {
  message: ChatMessage;
  isOwn: boolean;
  meta: CallMetadata;
}) {
  const { startCall, state } = useCall();
  const t = useT();
  const video = meta.callType === 'video';
  const when = whenLabel(message.createdAt, t);
  // Only the caller cancels; everyone else just missed it.
  const missedByMe =
    !isOwn && (meta.outcome === 'missed' || meta.outcome === 'cancelled');

  let title: string;
  if (meta.outcome === 'completed') {
    title = t(video ? 'chat.call.videoDuration' : 'chat.call.voiceDuration', {
      duration: minutes(t, meta.durationSeconds),
    });
  } else if (missedByMe) {
    title = t(video ? 'chat.call.missedVideo' : 'chat.call.missedVoice');
  } else if (meta.outcome === 'declined') {
    title = t(isOwn ? 'chat.call.declined' : 'chat.call.youDeclined');
  } else if (meta.outcome === 'cancelled') {
    title = t('chat.call.youCancelled');
  } else {
    title = t('chat.call.noAnswer');
  }

  if (missedByMe) {
    return (
      <HStack
        id={`message-${message.id}`}
        alignSelf="center"
        spacing="10px"
        py={2}
        pl="14px"
        pr={2}
        my={2}
        borderRadius="18px"
        bg="accent.tint"
      >
        <Box color="accent" display="flex">
          <ArrowDownLeft
            size={18}
            strokeWidth={2.25}
            aria-label={t('chat.call.missed')}
            role="img"
          />
        </Box>
        <Box>
          <Text fontWeight={700} fontSize="15px" color="accent">
            {title}
          </Text>
          <Text fontSize="13px" color="muted">
            {when}
          </Text>
        </Box>
        <Button
          h="44px"
          px={4}
          isDisabled={state.status !== 'idle'}
          onClick={() =>
            void startCall(message.conversationId, meta.callType ?? 'voice')
          }
        >
          {t('chat.call.callBack')}
        </Button>
      </HStack>
    );
  }

  const Icon = meta.outcome === 'completed' ? (video ? Video : Phone) : X;
  return (
    <HStack
      id={`message-${message.id}`}
      alignSelf="center"
      spacing="10px"
      py="10px"
      px="14px"
      my={2}
      borderRadius="18px"
      bg="surface"
      borderWidth="1px"
      borderColor="line"
    >
      <Box
        w="36px"
        h="36px"
        borderRadius="full"
        bg="surface.subtle"
        color={meta.outcome === 'completed' ? 'ink.body' : 'muted'}
        display="flex"
        alignItems="center"
        justifyContent="center"
        aria-hidden="true"
      >
        <Icon size={18} />
      </Box>
      <Box>
        <Text fontWeight={700} fontSize="15px">
          {title}
        </Text>
        <Text fontSize="13px" color="muted">
          {when}
        </Text>
      </Box>
    </HStack>
  );
}
